/**
 * Rare and epic chest weapons (GDD section 8): laser cannon, geyser, remote control. The voodoo doll is a
 * projectile (projectile.ts).
 */
import { Physique, STATE_LIQUID, STATE_STATIC, FLAG_FLAMMABLE, cosDeg, sinDeg, nextInt, wakeRect, TRIG_ONE } from '@invaders/sim';
import { FP, FP_SHIFT, PFP, PFP_SHIFT, TICKS_PER_SECOND, type GameState, type Projectile, type Unit } from './state';
import { UNIT_H, UNIT_W, damageUnit, unitCenter } from './unit';
import { WEAPONS } from './weapons';
import { WEAPON_LASER, WEAPON_GEYSER } from './weapon-ids';
import { hitBarrel } from './barrel';
import { CHEST_H, CHEST_W, hitMimic } from './chests';

const { EMPTY, ICE, FIRE, WATER, STEAM } = Physique;

// --- laser cannon ---------------------------------------------------------------------------------

/**
 * The ray (author's rules, instead of the GDD's instant ray of 120 cells): it flies RAY_SPEED cells per tick with no
 * range, its damage to each unit it goes through. Ice reflects it, as many times as it meets ice; any other surface
 * (rock, sand, gravel, obsidian, wood, powder…) reflects it too, RAY_WALL_BOUNCES times, and the next one stops it;
 * wood, oil and black powder catch fire where it hits them. It stops if it bounces twice in the same spot (within
 * RAY_LOOP cells). It goes through liquids and gases (oil burns).
 */
export const RAY_SPEED = 18; // 20, slowed down by a tenth (author's choice)
export const RAY_LOOP = 4;
export const RAY_WALL_BOUNCES = 5;
/** Every ray ends after this many cells, whatever it meets (a safety). */
const RAY_MAX = 8192;

export function fireLaser(state: GameState, unit: Unit, angle: number): void {
  const [cx, cy] = unitCenter(unit);
  // Direction: one cell along the major axis per step, in 1/1024 of a cell.
  let dx = Math.trunc((cosDeg(angle) * 1024) / TRIG_ONE);
  let dy = -Math.trunc((sinDeg(angle) * 1024) / TRIG_ONE);
  const major = Math.max(Math.abs(dx), Math.abs(dy)) || 1;
  dx = Math.trunc((dx * 1024) / major);
  dy = Math.trunc((dy * 1024) / major);
  state.projectiles.push({
    x: (cx << PFP_SHIFT) + (PFP >> 1), y: (cy << PFP_SHIFT) + (PFP >> 1), vx: dx << 6, vy: dy << 6,
    weapon: WEAPON_LASER, owner: unit.id, age: 0, link: -1, hit: [unit.id], marks: [cx, cy],
  });
}

/**
 * Reflects the ray on the ice at (x, y) like on a mirror, its surface told by the 5 × 5 cells around: the open ones
 * (not solid) give the way out, snapped to one of 8 directions. A wall or a floor flips one component, a slope of 45°
 * turns the ray by a right angle (the mirrors of the Palais des glaces). False if the surface cannot be told.
 */
function reflect(state: GameState, p: Projectile, x: number, y: number): boolean {
  const { w, h, data } = state.world.physique;
  const matState = state.world.rules.materials.state;
  let nx = 0;
  let ny = 0;
  for (let dy = -2; dy <= 2; dy++) {
    for (let dx = -2; dx <= 2; dx++) {
      const cx = x + dx;
      const cy = y + dy;
      if (cx < 0 || cx >= w || cy < 0 || cy >= h || matState[data[cy * w + cx]] >= STATE_LIQUID) { nx += dx; ny += dy; }
    }
  }
  const ax = Math.abs(nx);
  const ay = Math.abs(ny);
  if (ax === 0 && ay === 0) return false;
  const sx = ax * 5 > ay * 12 || ay * 5 <= ax * 12 ? Math.sign(nx) : 0;
  const sy = ay * 5 > ax * 12 || ax * 5 <= ay * 12 ? Math.sign(ny) : 0;
  const dot = p.vx * sx + p.vy * sy;
  if (dot >= 0) return false; // not going into it
  if (sx !== 0 && sy !== 0) {
    p.vx -= dot * sx;
    p.vy -= dot * sy;
  } else if (sx !== 0) p.vx = -p.vx;
  else p.vy = -p.vy;
  return true;
}

/** One tick of a ray: RAY_SPEED steps of one cell. Returns true once it is over. */
export function updateRay(state: GameState, p: Projectile): boolean {
  const { w, h, data } = state.world.physique;
  const cells = state.world.cellState.data;
  const tables = state.world.rules.materials;
  const hit = p.hit ?? (p.hit = []);
  const marks = p.marks ?? (p.marks = []);
  const finish = (x: number, y: number) => {
    marks.push(x, y);
    state.events.push({ type: 'laser', path: marks.slice() });
    return true;
  };
  for (let k = 0; k < RAY_SPEED; k++) {
    const ox = p.x >> PFP_SHIFT;
    const oy = p.y >> PFP_SHIFT;
    p.x += p.vx;
    p.y += p.vy;
    const x = p.x >> PFP_SHIFT;
    const y = p.y >> PFP_SHIFT;
    if (x < 0 || x >= w || y < 0 || y >= h || p.age * RAY_SPEED + k > RAY_MAX) return finish(ox, oy);
    const i = y * w + x;
    const m = data[i];
    const flammable = (tables.flags[m] & FLAG_FLAMMABLE) !== 0;
    if (tables.state[m] < STATE_LIQUID) {
      // A surface: ice sends the ray back every time, the other walls RAY_WALL_BOUNCES times; what burns catches fire.
      if (flammable) { data[i] = FIRE; cells[i] = 0; wakeRect(state.world, x, y, x, y); }
      if (m !== ICE && (p.walls ?? 0) >= RAY_WALL_BOUNCES) return finish(x, y);
      // Twice in the same spot: it would loop; it stops.
      for (let b = 2; b < marks.length; b += 2) {
        if (Math.abs(marks[b] - ox) <= RAY_LOOP && Math.abs(marks[b + 1] - oy) <= RAY_LOOP) return finish(ox, oy);
      }
      if (m !== ICE) p.walls = (p.walls ?? 0) + 1;
      const vx0 = p.vx;
      const vy0 = p.vy;
      if (!reflect(state, p, x, y)) {
        // A surface too rough to tell: the side that was hit (a wall flips the horizontal direction, a floor the
        // vertical one).
        const sideX = x !== ox && tables.state[data[oy * w + x]] < STATE_LIQUID;
        const sideY = y !== oy && tables.state[data[y * w + ox]] < STATE_LIQUID;
        if (sideX || !sideY) p.vx = -p.vx;
        if (sideY || !sideX) p.vy = -p.vy;
      }
      p.x = (ox << PFP_SHIFT) + (PFP >> 1);
      p.y = (oy << PFP_SHIFT) + (PFP >> 1);
      // A bumpy wall (rock) may tell a way out that still goes into it: then back where it came from.
      const ax = (p.x + p.vx) >> PFP_SHIFT;
      const ay = (p.y + p.vy) >> PFP_SHIFT;
      if (ax >= 0 && ax < w && ay >= 0 && ay < h && tables.state[data[ay * w + ax]] < STATE_LIQUID) { p.vx = -vx0; p.vy = -vy0; }
      marks.push(ox, oy);
      continue;
    }
    if (flammable) { data[i] = FIRE; cells[i] = 0; wakeRect(state.world, x, y, x, y); }
    hitMimic(state, x, y);
    hitBarrel(state, x, y);
    for (const u of state.units) {
      if (!u.alive || hit.includes(u.id)) continue;
      const ux = u.x >> FP_SHIFT;
      const uy = u.y >> FP_SHIFT;
      if (x >= ux && x < ux + UNIT_W && y >= uy && y < uy + UNIT_H) { hit.push(u.id); damageUnit(state, u, WEAPONS[WEAPON_LASER].damage, 'direct'); }
    }
  }
  p.age++;
  return false;
}

// --- geyser ---------------------------------------------------------------------------------------

/** GDD: the column springs for 2 s. */
export const GEYSER_TICKS = 2 * TICKS_PER_SECOND;
/** Height of the column, and of the launch it gives (cells per tick, fixed-point). */
export const GEYSER_HEIGHT = 60;
export const GEYSER_LAUNCH = 5 * FP;
/** Cells of water and steam thrown per tick: 6 for the GDD's width, the column is three times as wide. */
const GEYSER_FLOW = 18;
/** It springs this long after the start of the next player's turn (author's rule); the same after being put in the sandbox. */
export const GEYSER_DELAY = 5 * TICKS_PER_SECOND;

/** A geyser at the point: it will spring from the first solid ground under it. */
export function startGeyser(state: GameState, x: number, y: number): void {
  const { w, h, data } = state.world.physique;
  const matState = state.world.rules.materials.state;
  const cx = Math.min(w - 1, Math.max(0, x));
  let gy = Math.max(0, y);
  while (gy < h && matState[data[gy * w + cx]] !== STATE_STATIC) gy++;
  state.geysers.push({ x: cx, y: gy, ticks: GEYSER_TICKS, wait: GEYSER_DELAY, turn: state.match ? state.match.turn : -1 });
}

/** True if cell (x, y) is in the column of a geyser. */
function inColumn(g: { x: number; y: number }, x: number, y: number, half: number): boolean {
  return x >= g.x - half && x <= g.x + half && y < g.y && y >= g.y - GEYSER_HEIGHT;
}

export function updateGeysers(state: GameState): void {
  const { w, data } = state.world.physique;
  const cells = state.world.cellState.data;
  const rng = state.world.rng;
  const half = WEAPONS[WEAPON_GEYSER].radius;
  for (let k = state.geysers.length - 1; k >= 0; k--) {
    const g = state.geysers[k];
    if (state.match?.turn === g.turn) continue; // the next player's turn has not begun
    if (g.wait > 0) { g.wait--; continue; }
    g.ticks--;
    if (g.ticks < 0) { state.geysers.splice(k, 1); continue; }
    // Water and steam jet up the column; the steam falls back as rain later.
    for (let n = 0; n < GEYSER_FLOW; n++) {
      const x = g.x - half + nextInt(rng, 2 * half + 1);
      const y = g.y - 1 - nextInt(rng, GEYSER_HEIGHT);
      if (x < 0 || x >= w || y < 0) continue;
      const i = y * w + x;
      if (data[i] === EMPTY) { data[i] = (n & 1) === 0 ? WATER : STEAM; cells[i] = 0; }
    }
    wakeRect(state.world, g.x - half - 1, g.y - GEYSER_HEIGHT - 1, g.x + half + 1, g.y);
    // Units, chests and projectiles in the column are thrown up.
    for (const u of state.units) {
      if (!u.alive) continue;
      const [ux, uy] = unitCenter(u);
      if (Math.abs(ux - g.x) <= half + (UNIT_W >> 1) && uy < g.y + 2 && uy >= g.y - GEYSER_HEIGHT) { u.vy = -GEYSER_LAUNCH; u.walkAcc = 0; }
    }
    for (const c of state.chests) {
      const cx = (c.x >> FP_SHIFT) + (CHEST_W >> 1);
      const cy = (c.y >> FP_SHIFT) + (CHEST_H >> 1);
      if (Math.abs(cx - g.x) <= half + (CHEST_W >> 1) && cy < g.y + 2 && cy >= g.y - GEYSER_HEIGHT) c.vy = -GEYSER_LAUNCH;
    }
    for (const p of state.projectiles) {
      // Synergy "Catapulte": a voodoo doll crossing a geyser is thrown up (its linked enemy with it).
      if (inColumn(g, p.x >> PFP_SHIFT, p.y >> PFP_SHIFT, half + 2)) p.vy = -(GEYSER_LAUNCH * (PFP / FP));
    }
  }
}

// --- remote control -------------------------------------------------------------------------------

export interface RemoteMove {
  /** Centre of the square to lift. */
  sx: number;
  sy: number;
  /** Centre where it goes down. */
  tx: number;
  ty: number;
  /** Rotation in steps of 15°. */
  rot: number;
}

/**
 * A square of 96 × 96 cells (32 in the GDD, tripled by the author), moved anywhere on the map (the GDD: up to 60
 * cells; the author removed the limit).
 */
export const REMOTE_SIZE = 96;
export const REMOTE_STEP_DEG = 15;

/** Why a move is refused, or null if it is valid (the client shows it before the drop). */
export function remoteRefusal(state: GameState, move: RemoteMove): string | null {
  // GDD: it cannot be dropped on a unit: a cell of a unit's body under the square as it is put down, turned (the same
  // rule as the drop in moveSquare). A unit merely near it does not stop it. The square carries no unit (author's
  // rule), so the ones standing in it count too.
  const [cos, sin] = remoteTurn(move.rot);
  for (const u of state.units) {
    if (!u.alive) continue;
    const ux0 = u.x >> FP_SHIFT;
    const uy0 = u.y >> FP_SHIFT;
    for (let y = uy0; y < uy0 + UNIT_H; y++) {
      for (let x = ux0; x < ux0 + UNIT_W; x++) {
        if (squareCell(x - move.tx, y - move.ty, cos, sin) >= 0) return 'the square would land on a unit';
      }
    }
  }
  return null;
}

/** cos and sin (TRIG_ONE) of the remote's rotation, in steps of 15°. */
function remoteTurn(rot: number): [number, number] {
  const deg = ((Math.trunc(rot) % 24) + 24) % 24 * REMOTE_STEP_DEG;
  return [cosDeg(deg), sinDeg(deg)];
}

/**
 * The cell of the lifted square that lands at offset (dx, dy) from the drop point (inverse rotation), as an index
 * in the square; -1 if none does.
 */
function squareCell(dx: number, dy: number, cos: number, sin: number): number {
  const half = REMOTE_SIZE >> 1;
  const ux = Math.round((dx * cos + dy * sin) / TRIG_ONE + half - 0.5);
  const uy = Math.round((-dx * sin + dy * cos) / TRIG_ONE + half - 0.5);
  return ux < 0 || ux >= REMOTE_SIZE || uy < 0 || uy >= REMOTE_SIZE ? -1 : uy * REMOTE_SIZE + ux;
}

/**
 * Lifts the square and puts it down turned: every cell as it is (solids, powders, liquids, gases, fire). Only the
 * terrain moves (author's rule; the GDD carries the units, mines and chests too): what stood in the square stays
 * where it was and falls into the hole. Liquids and powders flow as soon as it is down.
 */
export function moveSquare(state: GameState, move: RemoteMove): void {
  if (remoteRefusal(state, move) !== null) return;
  const { w, h, data } = state.world.physique;
  const cells = state.world.cellState.data;
  const half = REMOTE_SIZE >> 1;
  const sx0 = move.sx - half;
  const sy0 = move.sy - half;
  // Lift: copy, then empty the source.
  const mat = new Uint8Array(REMOTE_SIZE * REMOTE_SIZE);
  const st = new Uint8Array(REMOTE_SIZE * REMOTE_SIZE);
  for (let y = 0; y < REMOTE_SIZE; y++) {
    for (let x = 0; x < REMOTE_SIZE; x++) {
      const gx = sx0 + x;
      const gy = sy0 + y;
      if (gx < 0 || gx >= w || gy < 0 || gy >= h) continue;
      const i = gy * w + gx;
      mat[y * REMOTE_SIZE + x] = data[i];
      st[y * REMOTE_SIZE + x] = state.world.rules.materials.state[data[i]] === STATE_LIQUID ? 0 : cells[i];
      data[i] = EMPTY;
      cells[i] = 0;
    }
  }
  // Drop, turned: each destination cell takes the source cell it comes from (rotation by integer tables).
  const [cos, sin] = remoteTurn(move.rot);
  const reach = Math.trunc((half * 181) / 128) + 1;
  for (let dy = -reach; dy <= reach; dy++) {
    for (let dx = -reach; dx <= reach; dx++) {
      const k = squareCell(dx, dy, cos, sin);
      const gx = move.tx + dx;
      const gy = move.ty + dy;
      if (k < 0 || gx < 0 || gx >= w || gy < 0 || gy >= h) continue;
      data[gy * w + gx] = mat[k];
      cells[gy * w + gx] = st[k];
    }
  }
  wakeRect(state.world, sx0 - 1, sy0 - 1, sx0 + REMOTE_SIZE, sy0 + REMOTE_SIZE);
  wakeRect(state.world, move.tx - reach - 1, move.ty - reach - 1, move.tx + reach + 1, move.ty + reach + 1);
  state.events.push({ type: 'remote', sx: move.sx, sy: move.sy, tx: move.tx, ty: move.ty, rot: move.rot });
}

